import type { ItemView, LearnerClient, LearnerView } from "@tutor/live";
import type { FractionAnswer } from "@tutor/ui/widgets";
import { createContext, useContext } from "react";
import { itemLocked } from "../lesson/ItemCard";

/**
 * What an interactive widget may see and do. Widgets never grade (the engine and actor
 * do) and never carry item content: a ChoiceQuestion names an item by id and shows it
 * only when it is `item`, the item the engine presented last.
 */
export interface WidgetActions {
  /** The item the engine presented last (see `widgetItem`), or `null`. */
  readonly item: ItemView | null;
  /** The child picked choice `index` of `itemId` in a ChoiceQuestion. */
  answer(itemId: string, index: number): void;
}

const none: WidgetActions = {
  // Rendering without a live client (preview): no item, so a ChoiceQuestion shows nothing.
  item: null,
  answer() {
    // Nothing to answer without a client.
  },
};

export const WidgetActionsContext = createContext<WidgetActions>(none);

export function useWidgetActions(): WidgetActions {
  return useContext(WidgetActionsContext);
}

/**
 * The item a widget may reference: the one the actor holds as current, which is the
 * prefetched `upNext` while the finished item is still on the canvas.
 */
export function widgetItem(view: Pick<LearnerView, "item" | "upNext">): ItemView | null {
  return view.upNext ?? view.item;
}

/**
 * Sends a ChoiceQuestion pick as an answer, through the same `answer.submit` path as a
 * tap on the item card, so it is checked and graded the same way. Only for `item`, a
 * choice item that still takes answers, and one of its choices; anything else is
 * ignored. Returns whether an answer was sent.
 */
export function answerFromWidget(
  client: Pick<LearnerClient, "answer">,
  item: ItemView | null,
  itemId: string,
  index: number,
): boolean {
  if (item?.itemId !== itemId || item.answer.kind !== "choice" || itemLocked(item)) return false;
  if (!Number.isInteger(index) || index < 0 || index >= item.answer.choices.length) return false;
  void client.answer(itemId, String(index));
  return true;
}

/**
 * The exercise's answer box, shared with the workbench's widgets so a FractionBars there
 * follows what the child types and can write a tapped fraction back. `null` everywhere
 * else (the conversation, a choice item, a finished one).
 */
export const AnswerDraftContext = createContext<FractionAnswer | null>(null);

export function useAnswerDraft(): FractionAnswer | null {
  return useContext(AnswerDraftContext);
}
